/**
 * A row of status pips with a `+N` overflow marker, built from pooled sprites
 * (plan 15 §2.4, phase 1).
 *
 * Pixi 8 rebuilds the whole stage when a Graphics is redrawn or a `visible`
 * flag flips (plan 13 §12). The pips used to be one Graphics cleared and
 * redrawn every frame, so a target with a status rebuilt the stage whenever
 * it moved or a pip faded. A pip row is a fixed pool of sprites made once:
 * every update is a position, tint or alpha write, and unused slots sit at
 * alpha 0.
 */
export interface PipSlot {
  /** Pip centre in world px. */
  x: number;
  y: number;
  color: number;
  /** 0..1 — the remaining-duration fade. */
  alpha: number;
}

export interface PipOverflow {
  /** Statuses past the row's capacity; shown as `+N` (capped at +99). */
  count: number;
  /** Centre of the `+N` marker in world px. */
  x: number;
  y: number;
}

export interface PipRowStyle {
  /** Pip slots in the pool (the config's maxPips). */
  capacity: number;
  /** Pip diameter in world px. */
  pipSize: number;
  /** `+N` glyph height in world px. */
  overflowFontSize: number;
  /** `+N` fill colour (a tint on white glyphs). */
  overflowColor: number;
  depth: number;
}

export interface IPipRow {
  /** Show `pips` (extra entries past capacity are ignored) and an optional
   *  overflow marker. Moves, tints and fades pooled sprites only. */
  update(pips: readonly PipSlot[], overflow: PipOverflow | null): void;
  /** Every slot to alpha 0. */
  hide(): void;
  destroy(): void;
}

type PipRowFactory = (style: PipRowStyle) => IPipRow;

let pixiFactory: PipRowFactory | null = null;

export function registerPixiPipRowFactory(fn: PipRowFactory): void {
  pixiFactory = fn;
}

export function createPipRow(style: PipRowStyle): IPipRow {
  if (!pixiFactory) {
    throw new Error('[createPipRow] Pixi factory not registered. Call registerAllPixiFactories() at boot.');
  }
  return pixiFactory(style);
}

/** The overflow marker's text: `+N`, capped at `+99` (two digit slots). */
export function overflowLabel(count: number): string {
  return `+${Math.max(0, Math.min(99, Math.floor(count)))}`;
}
